import {User} from './User';
import {parseJSON} from '../_helpers/fetch';
import {StepAssignee} from '../routes/AssigneeMaintenance';
import {DashboardConfiguration, Form} from '../types';

export type FlowFetchResponse = {
    status: number;
    ok: boolean;
    json: any;
};

export class Flow {
    private static instance: Flow;
    private _cache: any = {};

    private baseUrl: string = '/p/flow';

    private requestHeaders: any = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    };

    private constructor() {
    }

    public static getInstance(): Flow {
        if (!Flow.instance) {
            Flow.instance = new Flow();
        }

        return Flow.instance;
    }

    /**
     * Simple caching layer for common queries
     * TODO: should be factored into its own class
     *
     * @param key
     * @private
     */
    private cacheGet(key: string): any {
        console.log('cacheGet - ', key);
        return this._cache.hasOwnProperty(key) ?
            this._cache[key]
            :
            false;
    }

    private cacheSave(key: string, data: any) {
        console.log('cacheSave - ', key);
        this._cache[key] = data;
    }

    public checkSession() {
        return fetch(this.baseUrl + '/user/check')
            .then(parseJSON);
    }

    public userLogin(email: string, password: string): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/user/login', {
            headers: this.requestHeaders,
            method: 'POST',
            body: JSON.stringify({email, password})
        })
            .then(parseJSON);
    }

    public sendOtp(email: string): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/user/otp', {
            headers: this.requestHeaders,
            method: 'POST',
            body: JSON.stringify({email})
        })
            .then(parseJSON);
    }

    public getWorkflowCategories(): Promise<FlowFetchResponse> {
        return this.fetch('/workflow-categories');
    }

    public getWorkflows(filterType?: string, filter?: string, opts?: string[]): Promise<FlowFetchResponse> {
        const queryString = (opts) ? '?' + opts.join('&') : '';
        const endpoint = '/workflows/' + (filterType || 'all') + '/' + (filter || 'all') + queryString;

        const cacheKey = 'getWorkflows-' + endpoint;
        const cachedData = this.cacheGet(cacheKey);
        if (cachedData !== false) {
            return Promise.resolve(cachedData);
        }

        return this.fetch(endpoint)
            .then((response: any) => {
                if (response.ok) {
                    this.cacheSave(cacheKey, response);
                }
                return response;
            })
    }

    public getTables(formId: string): Promise<FlowFetchResponse> {
        return this.fetch('/tables/' + formId);
    }

    public getTabledataDistinctValuesForColumn(tableId: string, column: any): Promise<FlowFetchResponse> {
        return this.fetch('/tables/' + tableId + '/data/' + column.name + '?distinct=1');
    }

    public getTabledata(tableId: string, filters: any = {}): Promise<FlowFetchResponse> {
        return this.fetch('/tables/' + tableId + '/data');
    }


//  Maintanence Table upload
    public tableUpload(tableId: string,  file: File) {
        const formData: FormData = new FormData();
        formData.append('file', file);
        return fetch(`${this.baseUrl}/api/tables/${tableId}/import`, {
            headers: {
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: formData
        })
            .then(parseJSON);
    }

    public saveTabledata(tableId: string, recordId: string, record: any): Promise<FlowFetchResponse> {
        return fetch(`${this.baseUrl}/api/tables/${tableId}/data/${recordId}`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(record)
        })
            .then(parseJSON)
    }

    public deleteTabledata(tableId: string, recordId: string): Promise<FlowFetchResponse> {
        return fetch(`${this.baseUrl}/api/tables/${tableId}/data/${recordId}`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'DELETE'
        })
            .then(parseJSON)
    }

    public saveWorkflowHome(id: string, workflowHome: any): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + `/api/form/${id}/workflow-home`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(workflowHome)
        })
            .then(parseJSON)
    }

    public getForm(id?: string, queryString: string = '') {
        // only hit cache for default form calls
        let cacheKey = '';
        if (queryString === '') {
            cacheKey = 'getForm-' + id;
            const cachedData = this.cacheGet(cacheKey);
            if (cachedData !== false) {
                return Promise.resolve(cachedData);
            }
        }

        return this.fetch('/form/' + id + queryString)
            .then((response: any) => {
                if (response.ok && cacheKey) {
                    this.cacheSave(cacheKey, response);
                }
                return response;
            })
    }

    public getInboxForms() {
        return this.fetch('/inbox-forms');
    }

    public getInboxEntries(workflowId = '', fieldIds = '') {
        return this.fetch(`/inbox?form_id=${workflowId}&field_ids=${fieldIds}` );
    }

    public search(mine: boolean = false, filters: any) {
        const mineParam = (mine) ? '1' : '0';
        return fetch(this.baseUrl + '/api/search?mine=' + mineParam, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(filters)
        })
            .then(parseJSON)
    }

    public dataform(workflowId: number, dataformId: number, filters: any) {
        return fetch(this.baseUrl + `/api/dataform/${workflowId}/${dataformId}`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(filters)
        })
            .then(parseJSON)
    }

    public getEntry(entryId?: string) {
        return this.fetch('/entry/' + entryId);
    }

    public getWorkflowDiagram(formId: number, entryId?: number) {
        return this.fetch('/workflow-diagram/' + formId + '/' + entryId);
    }

    // NOTE - function does not appear to be used, remove
    public getEntries(entryIds: string[]) {
        return this.fetch('/entries?ids=' + entryIds.join(','));
    }

    public save(formId: string, formValues: any) {
        console.log('serializing', formValues);
        return fetch(this.baseUrl + '/api/save/' + formId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(formValues)
        })
            .then(parseJSON);
    }

    public update(formId: string, action: string, formValues: any) {
        return fetch(this.baseUrl + `/api/update/${formId}?action=${action}`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(formValues)
        })
            .then(parseJSON);
    }

    public adminAction(entryId: string, action: string, data: any = null) {
        return fetch(this.baseUrl + `/api/admin-operation/${entryId}?action=${action}`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: data === null ? '' : JSON.stringify(data)
        })
            .then(parseJSON);
    }

    public userChangeStep(entryId: string, stepId: string|number) {
        const postBody = stepId ? {stepId} : {};

        return fetch(this.baseUrl + `/api/user-change-step/${entryId}`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(postBody)
        })
            .then(parseJSON);
    }

    public upload(formId: number, file: File) {
        const formData: FormData = new FormData();
        formData.append('file', file);
        formData.append('form_id', String(formId));

        return fetch(this.baseUrl + '/api/upload', {
            headers: {
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: formData
        })
            .then(parseJSON);
    }

    public nestedFormExcelUpload(formId: number, fieldId: number, file: File): Promise<FlowFetchResponse> {
        const formData: FormData = new FormData();
        formData.append('file', file);
        formData.append('form_id', String(formId));
        formData.append('field_id', String(fieldId));

        return fetch(this.baseUrl + '/api/nested-form-excel-upload', {
            headers: {
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: formData
        })
            .then(parseJSON);
    }

    public getSearchFilters() {
        const cacheKey = 'getSearchFilters';
        const cachedData = this.cacheGet(cacheKey);
        if (cachedData !== false) {
            return Promise.resolve(cachedData);
        }

        return this.fetch('/search-filters')
            .then((response: any) => {
                if (response.ok) {
                    this.cacheSave(cacheKey, response);
                }
                return response;
            })
    }

    // NOTE - function does not appear to be used, remove
    public submitFeedback(type: 'general' | 'workflow', workflowId: string, message: string): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/submit-feedback', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                type,
                workflowId,
                message
            })
        })
            .then(parseJSON);
    }

    // NOTE - function does not appear to be used, remove
    public saveEvents(events: object[]): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/events', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(events)
        })
            .then(parseJSON)
    }

    public saveComments(entryId: string, comments: string, cc: string[]): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/comments/' + entryId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                comments,
                cc
            })
        })
            .then(parseJSON);
    }

    public export(filters: any, selectedColumns: any = null): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/export', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                ...filters,
                selectedColumns
            })
        })
            .then(parseJSON);
    }

    public getEntryStepCycleTime(workflowId: number, entryId: number) {
        return this.fetch(`/report/entry-step-cycle-time/${workflowId}/${entryId}`);
    }

    public getReportMetricDetail(workflowId: number, metric: string, filters: any) {
        return fetch(this.baseUrl + '/api/report/' + workflowId + '/metric-detail/' + metric, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(filters)
        })
            .then(parseJSON);
    }

    public reportSteps(workflowId?: string): Promise<FlowFetchResponse> {
        // NOTE: this use to be a GET request, but the rest of the /report/ endpoints were changed
        // to POST and the ApiController has a generic /report/* handler, so this request
        // was updated to a POST so it could utilize the same handler
        return fetch(this.baseUrl + '/api/report/' + workflowId + '/steps', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST'
        })
            .then(parseJSON);
    }

    public reportMetrics(workflowId?: string, dashboardConfiguration?: DashboardConfiguration): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/report/' + workflowId + '/metrics', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(dashboardConfiguration)
        })
            .then(parseJSON);
    }

    public reportAveDurationByStep(workflowId?: string, dashboardConfiguration?: DashboardConfiguration, stepId?: string): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/report/' + workflowId + '/ave-duration-by-step', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                ...dashboardConfiguration,
                fid: stepId ? stepId : ''
            })
        })
            .then(parseJSON);
    }

    public reportAveStepDurationByMonth(workflowId?: string, dashboardConfiguration?: DashboardConfiguration, stepId?: string): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/report/' + workflowId + '/ave-step-duration-by-month', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                ...dashboardConfiguration,
                fid: stepId ? stepId : ''
            })
        })
            .then(parseJSON);
    }

    public reportOpenByStep(workflowId?: string, dashboardConfiguration?: DashboardConfiguration): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/report/' + workflowId + '/open-by-step', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(dashboardConfiguration)
        })
            .then(parseJSON);
    }

    public saveDropdownValues(form: Form, field: any): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/form/' + form.id + '/dropdown-values', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(field),
        })
            .then(parseJSON);
    }

    public getAssigneeSteps(workflowId: string): Promise<FlowFetchResponse> {
        return this.fetch('/step-assignees/steps/' + workflowId);
    };

    public getAssigneesForStep(stepId: string): Promise<FlowFetchResponse> {
        return this.fetch('/step-assignees/list/' + stepId);
    }

    public getAssigneeStepConfig(stepId: string): Promise<FlowFetchResponse> {
        return this.fetch('/step-assignees/step-config/' + stepId);
    }

    public addAssignees(stepId: string, assignees: any[]): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/step-assignees/add-assignees/' + stepId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(assignees),
        })
            .then(parseJSON);
    }

    public saveAssignee(assignee: StepAssignee): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/step-assignees/save/' + assignee.id, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify(assignee),
        })
            .then(parseJSON);
    }

    public deleteAssignee(assigneeId: number): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/step-assignees/assignee/' + assigneeId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'DELETE'
        })
            .then(parseJSON);
    }

    public exportAssigneesForWorkflow(workflowId: string): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/step-assignees/export/' + workflowId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST'
        })
            .then(parseJSON);
    }

    public getSavedSearches(workflowId: string): Promise<FlowFetchResponse> {
        return this.fetch('/searches/' + workflowId);
    }

    public saveSearch(state: any, workflowId: string, name: string, savedSearchId: number, saveAsPublic: boolean): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/searches/' + workflowId + '/' + savedSearchId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                name,
                state,
                saveAsPublic
            }),
        })
            .then(parseJSON);
    };

    public deleteSearch(savedSearchId: number) {
        return fetch(this.baseUrl + '/api/searches/' + savedSearchId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'DELETE'
        })
            .then(parseJSON);
    }

    public saveFavorite(workflowId: number) {
        return fetch(this.baseUrl + '/api/user/workflow-favorite', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({workflowId})
        })
            .then(parseJSON);
    }

    public deleteFavorite(workflowId: number) {
        return fetch(this.baseUrl + '/api/user/workflow-favorite/' + workflowId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'DELETE'
        })
            .then(parseJSON);
    }

    public getUsersForRole(workflowId: string, roleName: string): Promise<FlowFetchResponse> {
        return this.fetch(`/roles/${workflowId}/${roleName}/users`);
    };

    public getFormRoles(formId: string): Promise<FlowFetchResponse> {
        return this.fetch(`/roles?workflowId=${formId}`);
    };

    public deleteRoleFromUser(workflowId: string, roleName: string, email: string): Promise<FlowFetchResponse> {
        return this.updateUserRole(workflowId, roleName, email, 'remove');
    };

    public addRoleToUser(workflowId: string, roleName: string, email: string, sponsor: string): Promise<FlowFetchResponse> {
        return this.updateUserRole(workflowId, roleName, email, 'add', sponsor);
    };

    public updateUserAttributesForRole(workflowId: string, roleName: string, email: string, sponsor: string): Promise<FlowFetchResponse> {
        return this.updateUserRole(workflowId, roleName, email, 'update', sponsor);
    }

    public updateUserRole(workflowId: string, roleName: string, email: string, action: 'add' | 'remove' | 'update', sponsor: string = ''): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + `/api/roles`, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                email,
                roleName,
                action,
                sponsor,
                formId: workflowId
            }),
        })
            .then(parseJSON);
    };

    public getUserDelegations(): Promise<FlowFetchResponse> {
        return this.fetch('/delegations?current=1&upcoming=1');
    }

    public getDelegatableSteps(): Promise<FlowFetchResponse> {
        return this.fetch('/delegations/workflow-steps-for-user')
    }

    public saveDelegation(startDate: string, endDate: string, showOutOfOffice: boolean, delegations: any): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/delegations', {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: JSON.stringify({
                startDate,
                endDate,
                showOutOfOffice,
                delegations
            }),
        })
            .then(parseJSON);
    }

    public removeUserDelegation(delegationId: any): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/delegations/' + delegationId, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'DELETE'
        })
            .then(parseJSON);
    }

    public getPosts(tag: string): Promise<FlowFetchResponse> {

        const cacheKey = 'getPosts-' + tag;
        const cachedData = this.cacheGet(cacheKey);
        if (cachedData !== false) {
            return Promise.resolve(cachedData);
        }

        return this.fetch('/posts/' + tag)
            .then((response: any) => {
                if (response.ok) {
                    this.cacheSave(cacheKey, response);
                }
                return response;
            })
    };

    public uploadNewTorqueSkuToBuFile(file: File) {
        const formData: FormData = new FormData();
        formData.append('file', file);

        return fetch(this.baseUrl + '/api/torque-sku-to-bu', {
            headers: {
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: formData
        })
            .then(parseJSON);
    }

    public getSupportRequests(): Promise<FlowFetchResponse> {
        return this.fetch('/support/requests');
    }

    public getPageForSlug(slug: string): Promise<FlowFetchResponse> {
        return this.fetch('/page/' + slug);
    }

    public paOnboardingGetEmployees(): Promise<FlowFetchResponse> {
        return fetch(this.baseUrl + '/api/torque-sku-to-bu', {
            headers: {
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'POST',
            body: formData
        })
            .then(parseJSON);
    }

    public fetch(endpoint: string) {
        console.log('fetching ' + this.baseUrl + '/api' + endpoint);
        return fetch(this.baseUrl + '/api' + endpoint, {
            headers: {
                ...this.requestHeaders,
                'Authorization': 'Bearer ' + User.getInstance().getAuthToken()
            },
            method: 'GET'
        })
            .then(parseJSON)
            .then(this.validateResponse)
    }

    public validateResponse(response: FlowFetchResponse) {
        // if the login session / token has expired, clear local storage and refresh the page
        if (response.status === 406) {
            if (User.getInstance().getAuthToken()) {
                User.getInstance().deleteAuthToken();
                window.location.reload();
            }
        }
        return response;
    }
}